const isRecord = (value: any): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const unwrapInsightResponse = (payload: any): Record<string, any> | null => {
  let value = payload;

  // The entity-service endpoints have returned both direct payloads and
  // success/result (occasionally result/result) envelopes over time.
  for (let depth = 0; depth < 3 && isRecord(value); depth += 1) {
    if (isRecord(value.result)) {
      value = value.result;
      continue;
    }
    if (isRecord(value.data)) {
      value = value.data;
      continue;
    }
    break;
  }

  return isRecord(value) ? value : null;
};

const firstText = (...values: any[]): string | undefined => {
  const value = values.find(
    (candidate) => typeof candidate === 'string' && candidate.trim().length > 0,
  );
  return typeof value === 'string' ? value : undefined;
};

const normalizeTopMatches = (value: any): string[] =>
  Array.isArray(value)
    ? value
        .map((match: any) =>
          typeof match === 'string'
            ? match
            : match?.name || match?.entity_name || match?.entityName,
        )
        .filter(Boolean)
    : [];

export const normalizeOwnershipEvaluation = (
  payload: unknown,
): Record<string, any> | null => {
  const value = unwrapInsightResponse(payload);
  if (!value) return null;

  const nested = isRecord(value.ownership_info)
    ? value.ownership_info
    : isRecord(value.beneficial_ownership)
    ? value.beneficial_ownership
    : value;

  const summary = firstText(
    nested.summary,
    nested.beneficial_ownership_summary,
    nested.explanation,
  );
  const topMatches = normalizeTopMatches(
    nested.topMatches ?? nested.top_matches,
  );

  return {
    ...nested,
    ...(summary ? { summary: summary } : {}),
    ...(topMatches.length ? { topMatches: topMatches } : {}),
    risk: firstText(nested.risk, nested.entity_risk, nested.risk_level),
    checkedAt:
      nested.checkedAt ??
      nested.checked_at ??
      nested.evaluatedAt ??
      nested.evaluated_at,
  };
};

export const hasCompletedOwnershipEvaluation = (value: unknown): boolean => {
  const normalized = normalizeOwnershipEvaluation(value);
  if (!normalized) return false;

  return Boolean(
    normalized.checked === true ||
      normalized.summary ||
      normalized.evaluationSource ||
      normalized.evaluation_source ||
      normalized.checkedAt ||
      normalized.risk ||
      typeof normalized.ownership_found === 'boolean' ||
      typeof normalized.direct_match_count === 'number' ||
      typeof normalized.close_match_count === 'number',
  );
};

export const normalizeAdverseMediaEvaluation = (
  payload: unknown,
): Record<string, any> | null => {
  const value = unwrapInsightResponse(payload);
  if (!value) return null;

  const nested = isRecord(value.adverse_media_info)
    ? value.adverse_media_info
    : isRecord(value.adverse_media)
    ? value.adverse_media
    : isRecord(value.combined)
    ? value.combined
    : value;

  const summary = firstText(
    nested.summary,
    nested.adverse_media_summary,
    nested.explanation,
  );

  return {
    ...nested,
    ...(summary ? { summary: summary } : {}),
    findings: Array.isArray(nested.findings) ? nested.findings : [],
    checkedAt:
      nested.checkedAt ??
      nested.checked_at ??
      nested.evaluatedAt ??
      nested.evaluated_at,
  };
};
